import {
  DialogContent,
  Dialog,
  DialogTrigger,
  LinkButton,
  DialogHeader,
  GhostIconButton,
} from "@certn/cascadia-react";
import { Modal, Stack, Text } from "@certn/cascadia";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { css } from "@emotion/css";
import { useClientPortalAPI } from "@certn/api";
import { isValidEmploymentVerificationCriteria } from "../../pages/adjudication/types/employment-verification-criteria-schema-v1";
import { isValidBasicCanadianCriminalRecordCriteria } from "../../pages/adjudication/types/basic-canadian-criminal-record-criteria-schema-v1";
import { CheckTypeSelector } from "./CheckTypeSelector";
import { PreviewBasicCanadianCriminalRecordCheckRuleset } from "./PreviewBasicCanadianCriminalRecordCheckRuleset";
import { PreviewEmploymentVerificationsCheckRuleset } from "./PreviewEmploymentVerificationsCheckRuleset";
import { PreviewRubricErrorSectionMessage } from "./PreviewRubricErrorSectionMessage";
import { RubricRuleset } from "./types";
import { shouldShowPreviewError } from "./utils";
import {
  BASIC_CANADIAN_CRIMINAL_RECORD_CHECK_TYPES,
  EMPLOYMENT_VERIFICATION_CHECK_TYPES,
} from "../../pages/adjudication/constants";
import { getCheckTypeName } from "../../pages/adjudication/utils";

type PreviewRubricModalProps = {
  rulesets: RubricRuleset[];
  triggerComponent?: React.ReactNode;
};

export function PreviewRubricModal({
  rulesets,
  triggerComponent,
}: PreviewRubricModalProps) {
  const { t } = useTranslation();

  // Create options for the select dropdown
  const checkTypeOptions = useMemo(
    () =>
      rulesets.map((ruleset) => ({
        id: ruleset.id,
        checkType: ruleset.check_type,
        name: getCheckTypeName(ruleset.check_type, t),
        ruleset,
      })),
    [rulesets, t]
  );

  const [selectedCheckId, setSelectedCheckId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [selectedRule, setSelectedRule] = useState<string | null>(null);

  // Reactively resolve selectedCheckId - falls back to first option if invalid
  const resolvedSelectedCheckId = useMemo(() => {
    if (checkTypeOptions.length === 0) return null;
    const isValid = checkTypeOptions.some(
      (option) => option.id === selectedCheckId
    );
    return isValid ? selectedCheckId : checkTypeOptions[0].id;
  }, [checkTypeOptions, selectedCheckId]);

  const selectedRuleset = checkTypeOptions.find(
    (option) => option.id === resolvedSelectedCheckId
  )?.ruleset;

  // Get subcategories data for Canadian Criminal Record checks
  const { data: subcategoriesData } = useClientPortalAPI(
    rulesets.some((ruleset) =>
      BASIC_CANADIAN_CRIMINAL_RECORD_CHECK_TYPES.includes(ruleset.check_type)
    )
      ? "/api/internal/client-portal/adjudication-rubrics/subcategories/"
      : null,
    {
      check_type: selectedRuleset?.check_type || "",
    }
  );

  // Reset navigation state when check type changes
  useEffect(() => {
    setSelectedCategory(null);
    setSelectedCode(null);
    setSelectedRule(null);
  }, [resolvedSelectedCheckId]);

  const checkTypeSelectorProps = {
    checkTypeOptions,
    selectedCheckId: resolvedSelectedCheckId,
    onSelectionChange: setSelectedCheckId,
  };

  return (
    <DialogTrigger trigger={triggerComponent}>
      {!triggerComponent && (
        <LinkButton testID={"create-new-scoring-rubric-button"}>
          {t("Preview")}
        </LinkButton>
      )}
      <Modal testID={"scoring-rubric-preview-modal"}>
        <Dialog width="860px" className={css({ height: "600px" })}>
          {({ close }) => (
            <>
              <DialogHeader
                onClose={() => {
                  setSelectedCategory(null);
                  setSelectedCode(null);
                  setSelectedRule(null);
                  setSelectedCheckId(null);
                  close();
                }}
              >
                <Stack orientation="horizontal" align="center" gap="space-2">
                  {selectedCode &&
                    BASIC_CANADIAN_CRIMINAL_RECORD_CHECK_TYPES.includes(
                      selectedRuleset?.check_type || ""
                    ) && (
                      <GhostIconButton
                        size="small"
                        icon="chevron_left"
                        onPress={() => setSelectedCode(null)}
                        testID="back-to-codes-button"
                      />
                    )}
                  {selectedCategory &&
                    !selectedCode &&
                    BASIC_CANADIAN_CRIMINAL_RECORD_CHECK_TYPES.includes(
                      selectedRuleset?.check_type || ""
                    ) && (
                      <GhostIconButton
                        size="small"
                        icon="chevron_left"
                        onPress={() => setSelectedCategory(null)}
                        testID="back-to-categories-button"
                      />
                    )}
                  {selectedRule &&
                    EMPLOYMENT_VERIFICATION_CHECK_TYPES.includes(
                      selectedRuleset?.check_type || ""
                    ) && (
                      <GhostIconButton
                        size="small"
                        icon="chevron_left"
                        onPress={() => setSelectedRule(null)}
                        testID="back-to-rules-button"
                      />
                    )}
                  <Text style="global/modal-header">
                    {t("Scoring Rubric Preview")}
                  </Text>
                </Stack>
              </DialogHeader>
              <DialogContent>
                <Stack>
                  {selectedRuleset && (
                    <>
                      {EMPLOYMENT_VERIFICATION_CHECK_TYPES.includes(
                        selectedRuleset.check_type
                      ) &&
                        isValidEmploymentVerificationCriteria(
                          selectedRuleset.criteria
                        ) && (
                          <PreviewEmploymentVerificationsCheckRuleset
                            ruleset={{
                              ...selectedRuleset,
                              criteria: selectedRuleset.criteria,
                            }}
                            selectComponent={
                              <CheckTypeSelector {...checkTypeSelectorProps} />
                            }
                            selectedRule={selectedRule}
                            setSelectedRule={setSelectedRule}
                          />
                        )}
                      {BASIC_CANADIAN_CRIMINAL_RECORD_CHECK_TYPES.includes(
                        selectedRuleset.check_type
                      ) &&
                        isValidBasicCanadianCriminalRecordCriteria(
                          selectedRuleset.criteria
                        ) && (
                          <PreviewBasicCanadianCriminalRecordCheckRuleset
                            ruleset={{
                              ...selectedRuleset,
                              criteria: selectedRuleset.criteria,
                            }}
                            selectComponent={
                              <CheckTypeSelector {...checkTypeSelectorProps} />
                            }
                            selectedCategory={selectedCategory}
                            setSelectedCategory={setSelectedCategory}
                            selectedCode={selectedCode}
                            setSelectedCode={setSelectedCode}
                            subcategoriesData={subcategoriesData}
                          />
                        )}
                      {shouldShowPreviewError(selectedRuleset) && (
                        <PreviewRubricErrorSectionMessage
                          selectedRuleset={selectedRuleset}
                        />
                      )}
                    </>
                  )}
                </Stack>
              </DialogContent>
            </>
          )}
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
